import { CameraIcon } from "@heroicons/react/24/outline";
import { InlineBanner } from "components/ui/inline-banner";
import { PrimaryButton } from "components/ui/primary-button";
import { SecondaryButton } from "components/ui/secondary-button";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { describeCameraAccessError } from "./camera-access-error";

type CameraState =
    | { kind: "starting" }
    | { kind: "streaming" }
    | { kind: "failed"; message: string };

interface InBrowserCameraProps {
    requestCameraStream: () => Promise<MediaStream>;
    captureFrame: (video: HTMLVideoElement) => Promise<File>;
    onCapture: (photo: File) => void;
    onClose: () => void;
}

const stopStream = (stream: MediaStream): void => {
    stream.getVideoTracks().forEach(track => track.stop());
};

export const requestRearCameraStream = async (): Promise<MediaStream> =>
    navigator.mediaDevices.getUserMedia({
        audio: false,
        video: {
            facingMode: { ideal: "environment" },
            width: { ideal: 1920 },
            height: { ideal: 1080 },
        },
    });

export const InBrowserCamera: React.FC<InBrowserCameraProps> = ({
    requestCameraStream,
    captureFrame,
    onCapture,
    onClose,
}) => {
    const videoRef = useRef<HTMLVideoElement>(null);
    const [cameraState, setCameraState] = useState<CameraState>({ kind: "starting" });
    const [isFrameReady, setIsFrameReady] = useState(false);
    const [captureError, setCaptureError] = useState<string | null>(null);

    useEffect(() => {
        let disposed = false;
        let activeStream: MediaStream | null = null;
        requestCameraStream()
            .then(stream => {
                if (disposed) {
                    stopStream(stream);
                    return;
                }
                activeStream = stream;
                if (videoRef.current) {
                    videoRef.current.srcObject = stream;
                }
                setCameraState({ kind: "streaming" });
            })
            .catch((error: unknown) => {
                if (!disposed) {
                    setCameraState({
                        kind: "failed",
                        message: describeCameraAccessError(error),
                    });
                }
            });
        return () => {
            disposed = true;
            if (activeStream) {
                stopStream(activeStream);
            }
        };
    }, [requestCameraStream]);

    const handleCapture = useCallback(async () => {
        const video = videoRef.current;
        if (!video) {
            return;
        }
        setCaptureError(null);
        try {
            onCapture(await captureFrame(video));
        } catch (error) {
            setCaptureError(
                error instanceof Error
                    ? error.message
                    : "Couldn't capture the photo. Please try again.",
            );
        }
    }, [captureFrame, onCapture]);

    if (cameraState.kind === "failed") {
        return (
            <div className="space-y-3">
                <InlineBanner type="error">{cameraState.message}</InlineBanner>
                <SecondaryButton onClick={onClose}>Close</SecondaryButton>
            </div>
        );
    }

    return (
        <div className="space-y-3">
            <div className="border border-zinc-200 rounded-md overflow-hidden bg-black">
                <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    muted
                    aria-label="Camera preview"
                    className="w-full h-auto block"
                    onLoadedMetadata={() => setIsFrameReady(true)}
                />
            </div>
            <p className="text-sm text-zinc-600">
                {cameraState.kind === "starting"
                    ? "Starting the camera..."
                    : "Hold the card flat so it fills the frame, then capture the photo."}
            </p>
            {captureError && <InlineBanner type="error">{captureError}</InlineBanner>}
            <div className="flex flex-col sm:flex-row gap-2">
                <PrimaryButton onClick={handleCapture} disabled={!isFrameReady}>
                    <div className="flex items-center justify-center space-x-2">
                        <CameraIcon className="w-5 h-5" />
                        <span>Capture photo</span>
                    </div>
                </PrimaryButton>
                <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>
            </div>
        </div>
    );
};
